/**
 * GeoNexora's own user management service (UMS), as this app uses it: companies, their seats and
 * subscription (platform staff), and the people of a company with their roles (the company's admin).
 *
 * <p>GeoNexora runs its own UMS beside its backend (auth.geonexora.eu; on premises the customer's), so this
 * talks to it directly with the same token the map uses - exchanged for the audiences `geonexora` and `ums`
 * (`auth/umsSession.ts`). UMS decides every call from its own grants: `ums:tenant:create` to open a company,
 * `ums:member:create` to invite, `ums:assignment:admin` to hand out roles - only roles made of what the
 * person holds themselves. This screen only shows what it allowed.
 *
 * <p>What a company sees of our data (layers, hidden columns, credits) is not here: that is GeoNexora's
 * taxonomy, kept by geonexora-server (`layerAdmin.service.ts`).
 */
import { loadAuthConfig } from '@/auth/authConfig';
import { renewNow } from '@/auth/umsSession';
import i18n from '@/i18n';

export const PRODUCT = 'geonexora';

/** The features of GeoNexora a company can have, as the manifest names them. */
export const FEATURES = [
  'geonexora.core',
  'geonexora.platform-data',
  'geonexora.analysis',
  'geonexora.export',
  'geonexora.geocode',
] as const;

/**
 * The roles a person is given for the whole company. The workspace roles are handed out by sharing a
 * workspace, and the platform data team's is ours.
 */
export const COMPANY_ROLES = ['admin', 'analyst', 'viewer', 'company-reader'] as const;
/** UMS's own: manages the company's people. The first administrator gets it with the company. */
export const COMPANY_ADMIN_ROLE = 'tenant-admin';

export interface UmsSubscription {
  product: string;
  plan?: string | null;
  features: string[];
  seatLimit?: number | null;
  validUntil?: string | null;
}

export interface UmsTenant {
  id: string;
  slug: string;
  displayName: string;
  /** ACTIVE or SUSPENDED. */
  status: string;
  subscriptions: UmsSubscription[];
}

export interface UmsAssignment {
  id: string;
  principalId: string;
  roleId: string;
  roleKey: string;
  roleName: string;
  product: string;
  scopeId: string;
  scopeName: string;
  scopeType: string;
  validUntil?: string | null;
  source: string;
}

export interface UmsMember {
  principalId: string;
  displayName: string;
  email?: string | null;
  /** INVITED until the first sign-in, then ACTIVE; SUSPENDED keeps the roles and switches them off. */
  state: string;
  since?: string | null;
  assignments: UmsAssignment[];
  /** One of ours (Veralytiq), in the company to work there (Veramenta docs/en/62); takes no seat. */
  staff?: boolean;
}

/** The signed-in person as UMS knows them. */
export interface UmsMe {
  principalId: string;
  displayName: string;
  email?: string | null;
}

export interface UmsRole {
  id: string;
  product: string;
  key: string;
  name: string;
  template: boolean;
  managed: boolean;
  rules: string[];
}

export interface NewMember {
  email: string;
  displayName: string;
  locale?: string;
}

export interface UiAuthorization {
  tenantId: string;
  tenantName: string;
  screens: Record<string, string>;
  permissions: Record<string, string[]>;
}

/** The service's refusals a person can run into here, as sentences (i18n keys under `access.refused`). */
const REFUSALS: Record<string, string> = {
  'delegation.escalation': 'escalation',
  'delegation.self': 'self',
  'membership.last_admin': 'last_admin',
  'plan.seat_limit': 'seats',
  'authz.missing_permission': 'permission',
  'authz.wrong_tenant': 'permission',
  'membership.exists': 'exists',
  'tenant.slug_taken': 'tenant_exists',
  'tenant.id_taken': 'tenant_exists',
  'product.unknown': 'product_unknown',
  'member.not_staff': 'not_staff',
  'product.not_used': 'product_not_used',
};

export class UmsError extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number) {
    super(message);
  }
}

async function base(): Promise<string> {
  const config = await loadAuthConfig();
  if (config.mode !== 'ums' || !config.umsUrl) {
    throw new UmsError(i18n.t('access.refused.no_ums'), 'no_ums');
  }
  return config.umsUrl.replace(/\/$/, '');
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = `${await base()}${path}`;
  const attempt = () => fetch(url, {
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${localStorage.getItem('auth_token') ?? ''}`,
    },
  });
  let response = await attempt();
  if (response.status === 401 && (await renewNow())) {
    response = await attempt();
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { code?: string; detail?: string };
    const key = body.code ? REFUSALS[body.code] : undefined;
    throw new UmsError(key ? i18n.t(`access.refused.${key}`) : body.detail ?? `HTTP ${response.status}`, body.code, response.status);
  }
  if (response.status === 204 || response.status === 202) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const json = (value: unknown) => JSON.stringify(value);

/** A URL-safe short name for a company: "Bussum Gordijnen B.V." → "bussum-gordijnen-b-v". */
export function slugOf(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function geonexoraSubscription(t: UmsTenant): UmsSubscription | undefined {
  return t.subscriptions?.find((s) => s.product === PRODUCT);
}

/** Whether a member holds one of GeoNexora's roles, i.e. takes a seat. */
export function takesSeat(m: UmsMember): boolean {
  // Ours take none of the company's seats (Veramenta docs/en/62).
  return !m.staff && m.state !== 'SUSPENDED' && m.assignments.some((a) => a.product === PRODUCT);
}

export const ums = {
  // ---------------------------------------------------------------- the signed-in person

  /** What the signed-in person may do in a product, for showing or hiding screens. */
  authorization: (product: string): Promise<UiAuthorization> =>
    call(`/me/authorization?product=${encodeURIComponent(product)}`),

  /** Who is signed in, as UMS knows them. */
  me: async (): Promise<UmsMe> => {
    const me = await call<{ id: string; displayName: string; email?: string | null }>('/me');
    return { principalId: me.id, displayName: me.displayName, email: me.email };
  },

  // ---------------------------------------------------------------- companies (platform staff)

  tenants: (): Promise<UmsTenant[]> => call('/admin/v1/tenants'),

  tenant: (id: string): Promise<UmsTenant> => call(`/admin/v1/tenants/${id}`),

  /**
   * Opens a company: GeoNexora with its seats and features, and its first administrator, who is invited by
   * mail, manages the company's people from there and holds GeoNexora's own administrator role as well.
   */
  createTenant: (input: {
    slug: string;
    displayName: string;
    seatLimit: number | null;
    features: string[];
    plan?: string | null;
    firstAdmin?: NewMember | null;
  }): Promise<{ id: string }> =>
    call('/admin/v1/tenants', {
      method: 'POST',
      body: json({
        slug: input.slug,
        displayName: input.displayName,
        firstAdmin: input.firstAdmin ?? null,
        subscriptions: [{ product: PRODUCT, plan: input.plan ?? null, features: input.features, seatLimit: input.seatLimit }],
      }),
    }),

  setSubscription: (tenantId: string, s: { seatLimit: number | null; features: string[]; plan?: string | null; validUntil?: string | null }): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/subscriptions`, {
      method: 'PUT',
      body: json({ product: PRODUCT, plan: s.plan ?? null, features: s.features, seatLimit: s.seatLimit, validUntil: s.validUntil ?? null }),
    }),

  suspendTenant: (tenantId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/suspend`, { method: 'POST' }),

  reactivateTenant: (tenantId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/reactivate`, { method: 'POST' }),

  // ---------------------------------------------------------------- people of a company

  members: (tenantId: string): Promise<UmsMember[]> =>
    call(`/admin/v1/tenants/${tenantId}/members`),

  /** Invites somebody with the roles they start with, for the whole company (its root). */
  invite: (tenantId: string, member: NewMember, roleIds: string[]): Promise<{ principalId: string }> =>
    call(`/admin/v1/tenants/${tenantId}/members`, {
      method: 'POST',
      body: json({ member, assignments: roleIds.map((roleId) => ({ roleId, scopeId: tenantId })) }),
    }),

  /** Sends a pending invitation again with a fresh link (the old one stops working). */
  resendInvitation: (tenantId: string, principalId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/members/${principalId}/invitation`, { method: 'POST' }),

  suspendMember: (tenantId: string, principalId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/members/${principalId}/suspend`, { method: 'POST' }),

  reactivateMember: (tenantId: string, principalId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/members/${principalId}/reactivate`, { method: 'POST' }),

  /**
   * One of ours joins the company at once - no invitation, no mail - with these roles for the whole
   * company, and works there like an employee (Veramenta docs/en/62). For the platform's staff; without
   * `principalId`, the signed-in person.
   */
  joinAsStaff: (tenantId: string, roleIds: string[], principalId?: string): Promise<{ principalId: string }> =>
    call(`/admin/v1/tenants/${tenantId}/staff`, {
      method: 'POST',
      body: json({ principalId: principalId ?? null, roleIds }),
    }),

  removeMember: (tenantId: string, principalId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/members/${principalId}`, { method: 'DELETE' }),

  // ---------------------------------------------------------------- roles

  roles: (tenantId: string): Promise<UmsRole[]> => call(`/admin/v1/tenants/${tenantId}/roles`),

  /** A role for the whole company: the company's root scope has the company's id. */
  assign: (tenantId: string, principalId: string, roleId: string): Promise<{ id: string }> =>
    call(`/admin/v1/tenants/${tenantId}/assignments`, {
      method: 'POST',
      body: json({ principalId, roleId, scopeId: tenantId }),
    }),

  revoke: (tenantId: string, assignmentId: string): Promise<void> =>
    call(`/admin/v1/tenants/${tenantId}/assignments/${assignmentId}`, { method: 'DELETE' }),
};

/** The roles of a company a person can be given here: GeoNexora's company roles and the company admin. */
export function assignableRoles(roles: UmsRole[]): UmsRole[] {
  const order = [COMPANY_ADMIN_ROLE, ...COMPANY_ROLES] as string[];
  return roles
    .filter((r) => (r.product === PRODUCT && (COMPANY_ROLES as readonly string[]).includes(r.key) || !r.template && r.product === PRODUCT)
      || (r.product === 'ums' && r.key === COMPANY_ADMIN_ROLE))
    .sort((a, b) => {
      const ia = order.indexOf(a.key);
      const ib = order.indexOf(b.key);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.name.localeCompare(b.name);
    });
}

export default ums;
